import type { BlockNoteEditor } from "@blocknote/core";
import {
  dataUrlFormat,
  loadImage,
} from "./briefExportImageResolve";
import { buildBriefHtmlWithMedia, collectImageBlocks } from "./briefExportMedia";
import { buildPdfContent } from "./briefExportPdfContent";

export type BriefExportResult = {
  mediaTotal: number;
  mediaFailed: number;
};

const slugify = (name: string) =>
  (name || "master-brief")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "master-brief";

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const downloadBriefAsDoc = async (
  editor: BlockNoteEditor,
  campaignName: string
): Promise<BriefExportResult> => {
  const imageCount = collectImageBlocks(editor).length;
  const htmlBody = await buildBriefHtmlWithMedia(editor);
  const title = campaignName.trim() || "Master brief";
  const safeTitle = title.replace(/</g, "");

  const docHtml = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>${safeTitle}</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1a1f36; line-height: 1.5; }
  img { max-width: 100%; height: auto; }
</style>
</head>
<body>
<h1>${safeTitle}</h1>
${htmlBody}
</body>
</html>`;

  triggerDownload(
    new Blob(["\ufeff", docHtml], { type: "application/msword;charset=utf-8" }),
    `${slugify(title)}-brief.doc`
  );

  // Approximate failures: remote imgs left without data: prefix after resolve
  const unresolved = (htmlBody.match(/firebasestorage\.googleapis\.com/g) || [])
    .length;
  return { mediaTotal: imageCount, mediaFailed: unresolved };
};

export const downloadBriefAsPdf = async (
  editor: BlockNoteEditor,
  campaignName: string
): Promise<BriefExportResult> => {
  const { jsPDF } = await import("jspdf");
  const title = campaignName.trim() || "Master brief";
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  const titleLines = doc.splitTextToSize(title, maxWidth) as string[];
  ensureSpace(titleLines.length * 20 + 16);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 20 + 8;

  doc.setDrawColor(227, 232, 238);
  doc.line(margin, y, pageWidth - margin, y);
  y += 16;

  const { items, mediaTotal, mediaFailed } = await buildPdfContent(editor);
  let wroteBody = false;

  for (const item of items) {
    if (item.kind === "image") {
      try {
        const image = await loadImage(item.dataUrl);
        const ratio = image.height / Math.max(image.width, 1);
        let width = Math.min(maxWidth, image.width || maxWidth);
        let height = Math.max(width * ratio, 1);
        const maxHeight = pageHeight - margin * 2;
        if (height > maxHeight) {
          height = maxHeight;
          width = height / ratio;
        }

        ensureSpace(height + 24);
        doc.addImage(
          item.dataUrl,
          dataUrlFormat(item.dataUrl),
          margin,
          y,
          width,
          height
        );
        y += height + 8;
        wroteBody = true;

        if (item.caption) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.setTextColor(105, 115, 134);
          const captionLines = doc.splitTextToSize(
            item.caption,
            maxWidth
          ) as string[];
          ensureSpace(captionLines.length * 14 + 8);
          doc.text(captionLines, margin, y);
          y += captionLines.length * 14 + 12;
          doc.setTextColor(26, 31, 54);
        } else {
          y += 8;
        }
      } catch {
        // skip
      }
      continue;
    }

    doc.setFont(
      "helvetica",
      item.kind === "heading" ? "bold" : "normal"
    );
    doc.setFontSize(item.kind === "heading" ? 13 : 11);
    const lines = doc.splitTextToSize(item.text, maxWidth) as string[];
    ensureSpace(lines.length * 16 + 6);
    doc.setTextColor(26, 31, 54);
    doc.text(lines, margin, y);
    y += lines.length * 16 + 6;
    wroteBody = true;
  }

  if (!wroteBody) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("No content yet.", margin, y);
  }

  doc.save(`${slugify(title)}-brief.pdf`);
  return { mediaTotal, mediaFailed };
};
